import { makeAutoObservable, runInAction } from "mobx";
import type { ApiNotification, NotificationType } from "@hive/sdk";
import { i18next } from "../i18n";
import { hive } from "../lib/apiClient";
import { errorMessage } from "../lib/format";

const PAGE_SIZE = 40;

/**
 * In-app notifications. A REST snapshot (`notifications.list` + `unreadCount`)
 * seeds the list; the shared realtime SSE connection (`hive.realtime`) then
 * pushes new notifications and the server-authoritative unread count live.
 * Realtime reconnection + tab-visibility are handled by the SDK; on every
 * (re)connect we refetch a snapshot to reconcile anything missed while
 * disconnected (the server does not replay).
 */
export class NotificationStore {
	userId: string | null = null;
	items: ApiNotification[] = [];
	unread = 0;
	loading = false;
	error = "";

	private disposers: Array<() => void> = [];
	private started = false;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	/** Open realtime + load the initial snapshot for a signed-in member. Idempotent. */
	start(userId: string) {
		if (this.started && this.userId === userId) return;
		if (this.started) this.stop();
		this.userId = userId;
		this.started = true;

		const rt = hive.realtime;
		this.disposers.push(
			rt.on("notification", (n) => this.onIncoming(n)),
			rt.on("unread-count", ({ count }) => {
				runInAction(() => {
					this.unread = count;
				});
			}),
			// The server never replays missed events — reconcile a fresh snapshot
			// on every (re)connect.
			rt.onOpen(() => {
				void this.loadSnapshot(true);
			}),
		);
		rt.start();
		void this.loadSnapshot(false);
	}

	/** Tear down realtime + clear state (sign-out / guest). Idempotent. */
	stop() {
		if (!this.started && this.disposers.length === 0) {
			this.reset();
			return;
		}
		this.disposers.forEach((d) => d());
		this.disposers = [];
		this.started = false;
		hive.realtime.stop();
		this.reset();
	}

	private reset() {
		this.userId = null;
		this.items = [];
		this.unread = 0;
		this.loading = false;
		this.error = "";
	}

	private onIncoming(n: ApiNotification) {
		runInAction(() => {
			if (this.items.some((x) => x.id === n.id)) return;
			this.items = [n, ...this.items];
			// Optimistic bump; a following `unread-count` event corrects it to the
			// server-authoritative value.
			if (!n.readAt) this.unread += 1;
		});
	}

	private async loadSnapshot(silent: boolean) {
		if (!silent) {
			runInAction(() => {
				this.loading = true;
				this.error = "";
			});
		}
		try {
			const [page, unread] = await Promise.all([
				hive.notifications.list({ limit: PAGE_SIZE }),
				hive.notifications.unreadCount().catch(() => 0),
			]);
			runInAction(() => {
				this.items = page.items;
				this.unread = unread;
			});
		} catch (err) {
			if (!silent) {
				runInAction(() => {
					this.error = errorMessage(err, i18next.t("notifications.loadFailed"));
				});
			}
		} finally {
			if (!silent) {
				runInAction(() => {
					this.loading = false;
				});
			}
		}
	}

	/** Mark everything read (called when the viewer opens the notifications page). */
	async markAllRead() {
		const hadUnread = this.unread > 0 || this.items.some((n) => !n.readAt);
		const now = new Date().toISOString();
		runInAction(() => {
			this.unread = 0;
			this.items = this.items.map((n) => (n.readAt ? n : { ...n, readAt: now }));
		});
		if (hadUnread) await hive.notifications.markAllRead().catch(() => {});
	}

	async markTypesRead(types: NotificationType[]) {
		const typeSet = new Set(types);
		const unreadIds = new Set(
			this.items
				.filter((item) => !item.readAt && typeSet.has(item.type))
				.map((item) => item.id),
		);
		const now = new Date().toISOString();
		runInAction(() => {
			this.items = this.items.map((item) =>
				unreadIds.has(item.id) ? { ...item, readAt: now } : item,
			);
			this.unread = Math.max(0, this.unread - unreadIds.size);
		});

		try {
			// Per-bucket ack — markAllRead scopes to these TYPES (native since @hive/sdk 0.13.0).
			await hive.notifications.markAllRead({ types });
			void this.loadSnapshot(true);
		} catch {
			runInAction(() => {
				this.items = this.items.map((item) =>
					unreadIds.has(item.id) && item.readAt === now ? { ...item, readAt: null } : item,
				);
				this.unread += unreadIds.size;
			});
		}
	}

	async markRead(id: string) {
		const current = this.items.find((item) => item.id === id);
		if (!current || current.readAt) return;
		const now = new Date().toISOString();
		runInAction(() => {
			this.items = this.items.map((item) => (item.id === id ? { ...item, readAt: now } : item));
			this.unread = Math.max(0, this.unread - 1);
		});
		try {
			await hive.notifications.markRead(id);
		} catch {
			runInAction(() => {
				this.items = this.items.map((item) =>
					item.id === id && item.readAt === now ? { ...item, readAt: null } : item,
				);
				this.unread += 1;
			});
		}
	}
}

export const notificationStore = new NotificationStore();
